import { observer } from "mobx-react-lite";
import { Settings2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BulkActionButton } from "@/domains/documents/components/atoms/BulkActionButton";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useIssuesStore, usePMStore, useProjectMembersStore } from "@/stores/pm/hooks/usePMStore";
import { useProjects } from "@/domains/documents/hooks/useProjects";
import type { PMIssue, UserProfile } from "@/domains/pm/types";
import { IssueMenuItems } from "./IssueMenuItems";
import { DROPDOWN_MENU_KIT } from "./issueMenuKit";
import { useIssueMenuActions, commonIds, commonProjectId } from "./useIssueMenuActions";

interface IssueActionsMenuProps {
  /** Currently checked issue ids. */
  selectedIssueIds: number[];
  buildingId: number;
  /**
   * Render the trigger icon-only, matching the BulkActionBar's compact layout so
   * this action doesn't keep the toolbar from collapsing on a narrow viewport.
   */
  compact?: boolean;
}

/**
 * WHA-3428 — the toolbar "Actions" dropdown. Opens the same menu as a
 * right-click on a multi-selection, applied to all selected issues.
 */
export const IssueActionsMenu = observer(function IssueActionsMenu({
  selectedIssueIds,
  buildingId,
  compact = false,
}: IssueActionsMenuProps) {
  const { t } = useLanguage();
  const issuesStore = useIssuesStore();
  const pmStore = usePMStore();
  const projectMembers = useProjectMembersStore();
  const actions = useIssueMenuActions();

  const idSet = new Set(selectedIssueIds);
  const targetIssues: PMIssue[] = issuesStore.issues.filter((i) => idSet.has(i.id));

  const status = targetIssues.every((i) => i.status === targetIssues[0]?.status)
    ? targetIssues[0]?.status
    : undefined;
  const assigneeIds = commonIds(targetIssues.map((i) => (i.assignees ?? []).map((a) => a.id)));
  const disciplineIds = commonIds(targetIssues.map((i) => (i.disciplines ?? []).map((d) => d.id)));

  // WHA-3507: only show users assignable to every selected issue's project.
  // RLS would 403 a write to a project the user can't read, so present that
  // constraint upfront in the bulk picker instead of letting saves fail.
  const targetProjectIds = Array.from(
    new Set(targetIssues.map((i) => i.building_project_id))
  ).filter((id): id is number => Number.isFinite(id) && id > 0);
  const projectUsers: UserProfile[] = projectMembers.intersection(targetProjectIds);

  // Move-to-project targets — the building's projects (issue #11). The check
  // mark tracks the selection's shared project, hidden when it spans several.
  const { projects } = useProjects(String(buildingId));
  const projectOptions = projects.map((p) => ({
    id: Number(p.id),
    name: p.name,
    status: p.status,
  }));
  const currentProjectId = commonProjectId(targetIssues);

  const ensureData = () => {
    pmStore.loadDisciplines();
    for (const pid of targetProjectIds) {
      projectMembers.fetch(pid);
    }
  };

  return (
    <DropdownMenu onOpenChange={(open) => open && ensureData()}>
      <DropdownMenuTrigger asChild>
        <BulkActionButton
          icon={Settings2}
          label={t.pm.issues.contextMenu.actions}
          layout={compact ? "icon" : "vertical"}
          onClick={() => {
            /* Radix DropdownMenuTrigger handles open/close on click. */
          }}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="end" className="w-56">
        <IssueMenuItems
          kit={DROPDOWN_MENU_KIT}
          mode="multi"
          selectedCount={targetIssues.length}
          status={status}
          assigneeIds={assigneeIds}
          disciplineIds={disciplineIds}
          projects={projectOptions}
          currentProjectId={currentProjectId}
          users={projectUsers}
          disciplines={pmStore.disciplines}
          onSetStatus={(s) => actions.applyStatus(targetIssues, buildingId, s)}
          onAssigneesChange={(ids) =>
            actions.applyAssignees(targetIssues, buildingId, ids, assigneeIds)
          }
          onDisciplinesChange={(ids) =>
            actions.applyDisciplines(targetIssues, buildingId, ids, disciplineIds)
          }
          onSetStartDate={(d) => actions.applyStartDate(targetIssues, buildingId, d)}
          onSetDueDate={(d) => actions.applyDueDate(targetIssues, buildingId, d)}
          onMoveToProject={(pid) => actions.applyMoveToProject(targetIssues, buildingId, pid)}
          onAskFranklin={() => actions.askFranklin(targetIssues)}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
});
